import { DS_SHAPE_BORDER_RADIUS } from '@calm/evexia-design-tokens';
import {
    MediaPackProgram,
    MediaProgram,
    RbcCircleRec,
} from '@calm/evexia-web-sdk';
import { InfoOutlined } from '@mui/icons-material';
import { useLearnWhyDialog } from '@rhg/hooks/use-learn-why-dialog';
import planMessages from '@rhg/lang/messages/plan';
import {
    Button,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    SxProps,
    Theme,
    Typography,
} from '@rhg/libs/material';
import { useIntl } from 'react-intl';

const styleLearnWhy = {
    zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
    width: 24,
    height: 24,
};

const styleLearnWhyTextButton = {
    height: 46,
    p: 0,
    borderRadius: `${DS_SHAPE_BORDER_RADIUS.SMALL}px`,
    textAlign: 'start',
};

export interface LearnWhyButtonProps {
    isTopRecommendation?: boolean;
    recommendation?: RbcCircleRec;
    program?: MediaProgram;
    packProgram?: MediaPackProgram;
    sx?: SxProps<Theme>;
    variant?: 'icon' | 'text' | 'menuItem';
}
export function LearnWhyButton({
    isTopRecommendation,
    recommendation,
    program,
    variant = 'icon',
    packProgram,
}: LearnWhyButtonProps) {
    const { formatMessage } = useIntl();
    const { learnWhy } = useLearnWhyDialog();

    const handleClick = () => {
        const reason = recommendation?.reason;
        const packClass = recommendation?.additionalData?.packClassId;
        if (program && packClass && reason) {
            learnWhy({
                packClass,
                program,
                packProgram,
                isTopRecommendation: !!isTopRecommendation,
                reason,
            });
        }
    };

    if (
        !program ||
        !recommendation?.reason ||
        !recommendation?.additionalData?.packClassId
    ) {
        return null;
    }

    if (variant === 'text') {
        return (
            <>
                <Button
                    sx={styleLearnWhyTextButton}
                    trackButton="LearnWhy"
                    size="small"
                    onClick={handleClick}
                >
                    <Typography variant="body2" fontWeight="bold">
                        {formatMessage(planMessages['plan.learn_why'])}
                    </Typography>
                </Button>
            </>
        );
    }

    if (variant === 'menuItem') {
        return (
            <>
                <MenuItem trackButton="LearnWhy" onClick={handleClick}>
                    <ListItemIcon>
                        <InfoOutlined />
                    </ListItemIcon>
                    <ListItemText>
                        <Typography variant="body1" color="text.body">
                            {formatMessage(
                                planMessages[
                                    'plan.learn_why_recommended_simple'
                                ]
                            )}
                        </Typography>
                    </ListItemText>
                </MenuItem>
            </>
        );
    }

    return (
        <>
            <IconButton
                aria-label={formatMessage(
                    planMessages['plan.learn_why_recommended'],
                    {
                        title: program?.title,
                    }
                )}
                sx={styleLearnWhy}
                trackButton={
                    isTopRecommendation
                        ? 'TopRecommendationInfo'
                        : 'AdditionalRecommendationsInfo'
                }
                onClick={handleClick}
            >
                <InfoOutlined />
            </IconButton>
        </>
    );
}
